import { Grid, useTheme, Stack } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { ProductManager } from '../../../services/api/ProductManager'
import { EventInPlanningDto } from '../../../services/api/sharedTypes/Event'
import EventDetailForPlanning from './EventDetailForPlanning'
import EventSmallDetailForPlanning from './EventSmallDetailForPlanning'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import { ContractorBriefDto } from '../../../services/api/sharedTypes/Contractor'
import { ProductWithStatisticsDto } from '../../../services/api/sharedTypes/Product'
import { ContractorManager } from '../../../services/api/ContractorManager'
import { EventManager } from '../../../services/api/EventManager'
import Map from './Map/Map'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'

interface EventPlanningProps {
    events: EventInPlanningDto[]
    onRefetchEvents: () => void
    onChange: (events: EventInPlanningDto[]) => void
    userIsAdmin: boolean
}

const EventPlanning: React.FC<EventPlanningProps> = ({ events, userIsAdmin, onRefetchEvents, onChange }: EventPlanningProps) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [isMapFocused, setIsMapFocused] = useState<boolean>(false)
    const [productList, setProductList] = useState<ProductWithStatisticsDto[]>()
    const [contractorList, setContractorList] = useState<ContractorBriefDto[]>()

    const [loadingProducts, setLoadingProducts] = useState<boolean>(false)
    const [loadingContractors, setLoadingContractors] = useState<boolean>(false)

    useEffect(() => {
        const getProducts = async () => {
            try {
                setLoadingProducts(true)
                setProductList([])
                const productRes = await ProductManager.getManager().getProductListAsync()
                if (!productRes) throw new Error('Produkty nenalezeny')
                setProductList(productRes.products)
            } catch (error) {
                dispatch(displayMessage({ message: error, type: 'error' }))
                setLoadingProducts(false)
            }
            setLoadingProducts(false)
        }

        const getContractors = async () => {
            try {
                setLoadingContractors(true)
                setContractorList([])
                const contractorRes = await ContractorManager.getManager().getContractorListAsync()
                if (!contractorRes) throw new Error('Pracovníci nenalezeni')
                setContractorList(contractorRes.contractors)
            } catch (error) {
                dispatch(displayMessage({ message: error, type: 'error' }))
                setLoadingContractors(false)
            }
            setLoadingContractors(false)
        }
        getProducts()
        getContractors()
    }, [setLoadingContractors, setLoadingProducts, setContractorList, setProductList, dispatch])

    const updateEvent = async (newEvent: EventInPlanningDto) => {
        try {
            const res = await EventManager.getManager().updateEventForPlanningAsync(newEvent.id, {
                isEverythingReady: newEvent.isEverythingReady,
                planningNote: newEvent.planningNote,
                products: newEvent.products.map((product) => ({
                    productId: product.productId,
                    costs: product.costs,
                    isConfirmed: product.isConfirmed,
                    revenue: product.revenue,
                    quantity: product.quantity,
                    operatingTime: product.operatingTime,
                })),
                contractors: newEvent.contractors.map((contractor) => ({
                    contractorId: contractor.contractorId,
                    gotPaid: contractor.gotPaid,
                    isConfirmed: contractor.isConfirmed,
                    costs: contractor.costs,
                })),
            })
            if (res) {
                onChange(events.map((event) => (event.id === res.id ? res : event)))

                dispatch(displayMessage({ message: 'Událost byla aktualizována', type: 'success' }))
            }
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    return (
        <Grid container bgcolor={theme.palette.background.default}>
            <Grid item xs={12} lg={isMapFocused ? 3 : 8} style={{ overflow: 'auto' }}>
                <Stack spacing={3} padding={isMapFocused ? 3 : 0}>
                    {events
                        .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())
                        .map((event) =>
                            !isMapFocused ? (
                                <EventDetailForPlanning
                                    key={event.id}
                                    event={event}
                                    onRefetch={onRefetchEvents}
                                    onEventChange={updateEvent}
                                    contractorList={contractorList}
                                    productList={productList}
                                    loadingContractors={loadingContractors}
                                    loadingProducts={loadingProducts}
                                    userIsAdmin={userIsAdmin}
                                />
                            ) : (
                                <EventSmallDetailForPlanning
                                    key={event.id}
                                    onRefetch={onRefetchEvents}
                                    onEventChange={updateEvent}
                                    event={event}
                                />
                            )
                        )}
                </Stack>
            </Grid>
            <Grid item xs={12} lg={isMapFocused ? 9 : 4} style={{ overflow: 'auto' }}>
                <div style={{ margin: 10 }}>
                    <div
                        style={{
                            position: 'absolute',
                            zIndex: 100,
                            padding: theme.spacing(2),
                            paddingTop: theme.spacing(3),
                            paddingBottom: theme.spacing(3),
                            backgroundColor: theme.palette.tableHeader.default,
                            cursor: 'pointer',
                            marginTop: '40vh',
                        }}
                        onClick={() => setIsMapFocused(!isMapFocused)}
                    >
                        {!isMapFocused ? <ArrowBackIosNewRoundedIcon /> : <ArrowForwardIosRoundedIcon />}
                    </div>
                    <Map events={events} isMapFocused={isMapFocused} />
                </div>
            </Grid>
        </Grid>
    )
}

export default EventPlanning
